"use client"
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalOverlay,
  useDisclosure,
  Tooltip,
  useToast,
  Box,
  SkeletonCircle,
  SkeletonText,
  Select,
  Badge,
  FormErrorMessage,
  Alert,
  AlertIcon,
} from "@chakra-ui/react"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  addProjectAsync,
  deleteProjectAsync,
  fetchProjectsAsync,
  selectProjects,
  updateProjectAsync,
} from "./ProjectSlice"
import { useForm } from "react-hook-form"
import SpinnerLoad from "../../../common/Spinner"
import EmptyState from "../../components/Tester"
import { FaCode } from "react-icons/fa"

export default function Projects() {
  const dispatch = useDispatch()
  const projects = useSelector(selectProjects)

  const [selectedProject, setSelectedProject] = useState(null)
  const [isUpdate, setIsUpdate] = useState(false)
  const { isOpen: isProjectModalOpen, onOpen: onProjectModalOpen, onClose: onProjectModalClose } = useDisclosure()
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure()

  const hasFetchedProjects = useRef(false)

  const startYear = 2000
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1
  const [deletingProject, setDeletingProject] = useState(null)
  const [loading, setLoading] = useState(false) // submit/delete spinner
  const [isInitialLoading, setIsInitialLoading] = useState(true) // skeleton control
  const toast = useToast()

  // Years list (startYear --> currentYear)
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => startYear + i)

  const months = [
    { label: "January", value: 1 },
    { label: "February", value: 2 },
    { label: "March", value: 3 },
    { label: "April", value: 4 },
    { label: "May", value: 5 },
    { label: "June", value: 6 },
    { label: "July", value: 7 },
    { label: "August", value: 8 },
    { label: "September", value: 9 },
    { label: "October", value: 10 },
    { label: "November", value: 11 },
    { label: "December", value: 12 },
  ]

  // Helper: fetch and control skeleton
  const refreshProjects = async () => {
    setIsInitialLoading(true)
    try {
      await dispatch(fetchProjectsAsync())
    } finally {
      setIsInitialLoading(false)
    }
  }

  useEffect(() => {
    const fetchInitial = async () => {
      await refreshProjects()
    }
    if (!hasFetchedProjects.current) {
      hasFetchedProjects.current = true
      fetchInitial()
    } else {
      setIsInitialLoading(false)
    }
  }, [dispatch])

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      project_tag: "",
      project_status: "",
      role: "",
      skill_used: "",
      role_description: "",
      project_description: "",
      employment_type: "",
      team_size: "",
      work_from_year: "",
      work_till_year: "",
      work_from_month: "",
      work_till_month: "",
    },
  })

  // Open Add with clean defaults
  const openAddModal = () => {
    setIsUpdate(false)
    setSelectedProject(null)
    reset({
      project_tag: "",
      project_status: "",
      role: "",
      skill_used: "",
      role_description: "",
      project_description: "",
      employment_type: "",
      team_size: "",
      work_from_year: "",
      work_till_year: "",
      work_from_month: "",
      work_till_month: "",
    })
    onProjectModalOpen()
  }

  // Open Update with normalized prefilled values using reset
  const openUpdateModal = (project) => {
    if (!project) return
    setSelectedProject(project)
    setIsUpdate(true)
    reset({
      project_tag: project?.project_tag || "",
      project_status: project?.project_status || "",
      role: project?.role || "",
      skill_used: project?.skill_used || "",
      role_description: project?.role_description || "",
      project_description: project?.project_description || "",
      employment_type: project?.employment_type || "",
      team_size: project?.team_size != null ? String(project.team_size) : "",
      work_from_year: project?.work_from_year || "",
      work_till_year: project?.work_till_year || "",
      work_from_month: project?.work_from_month || "",
      work_till_month: project?.work_till_month || "",
    })
    onProjectModalOpen()
  }

  // Close the modal cleanly (avoid stale values)
  const handleCloseProjectModal = () => {
    if (loading) return // prevent closing while submitting
    setSelectedProject(null)
    setIsUpdate(false)
    reset({
      project_tag: "",
      project_status: "",
      role: "",
      skill_used: "",
      role_description: "",
      project_description: "",
      employment_type: "",
      team_size: "",
      work_from_year: "",
      work_till_year: "",
      work_from_month: "",
      work_till_month: "",
    })
    onProjectModalClose()
  }

  const handleSave = async (newProject) => {
    setLoading(true)
    try {
      await dispatch(addProjectAsync({ newProject })).unwrap()
      await refreshProjects() // ensure immediate UI refresh
      handleCloseProjectModal()
      toast({
        title: "Project Added",
        description: "Your project has been successfully added.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } catch (error) {
      console.error("Failed to add project", error)
      toast({
        title: "Error",
        description: error.message || "Failed to add project. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdate = async (projectData) => {
    setLoading(true)
    if (selectedProject) {
      try {
        await dispatch(
          updateProjectAsync({
            uuid: selectedProject.uuid,
            projectData,
          }),
        ).unwrap()
        await refreshProjects() // ensure immediate UI refresh
        handleCloseProjectModal()
        toast({
          title: "Project Updated",
          description: "Your project details have been successfully updated.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      } catch (error) {
        console.error("Failed to update project", error)
        toast({
          title: "Error",
          description: error.message || "Failed to update project. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      } finally {
        setLoading(false)
      }
    }
  }

  const handleFormSubmit = (data) => {
    if (isUpdate) {
      handleUpdate(data)
    } else {
      handleSave(data)
    }
  }

  const handleDelete = async (projectId) => {
    setLoading(true)
    try {
      await dispatch(deleteProjectAsync({ uuid: projectId })).unwrap()
      await refreshProjects() // ensure immediate UI refresh
      onDeleteModalClose()
      toast({
        title: "Project Deleted",
        description: "Your project has been successfully deleted.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } catch (error) {
      console.error("Failed to delete project", error)
      toast({
        title: "Error",
        description: error.message || "Failed to delete project. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setLoading(false)
    }
  }

  const openDeleteModal = (project) => {
    setDeletingProject(project)
    onDeleteModalOpen()
  }

  // Validation helpers
  const validateYear = (value) => {
    if (!value) return "Starting Year is required"
    const year = parseInt(value, 10)
    if (isNaN(year)) return "Invalid year"
    if (year < startYear || year > currentYear) return `Year must be between ${startYear} and ${currentYear}`
    return true
  }

  const validateMonth = (value) => {
    if (!value) return "Starting Month is required"
    const month = parseInt(value, 10)
    if (isNaN(month) || month < 1 || month > 12) return "Month must be between 1 and 12"
    const year = parseInt(getValues("work_from_year"), 10)
    if (year === currentYear && month > currentMonth) {
      return "Start month cannot be in the future"
    }
    return true
  }

  // Safe number/string validation
  const validateTeamSize = (value) => {
    const str = typeof value === "string" ? value.trim() : value == null ? "" : String(value).trim()
    if (!str) return "Team Size is required"
    if (!/^\d+$/.test(str)) return "Team size must be a number"
    const num = Number(str)
    if (!Number.isFinite(num)) return "Team size must be a number"
    if (num <= 0) return "Team size must be greater than 0"
    if (num > 5000) return "Please enter a reasonable team size"
    return true
  }

  return (
    <div className="" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="bg-mainBg ">
        <div className=" mx-auto ">
          <div className="bg-white rounded-xl w-full px-3 sm:px-5 py-4 shadow-sm  border-gray-100">
            {/* Header */}
            <div className="flex sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <div className="flex items-start gap-2">
                <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <FaCode />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl text-left font-semibold">Projects</h3>
                  <p className="text-xs text-gray-500">Add and manage your project experience</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Tooltip label="Add new project" aria-label="Add project tooltip">
                  <Button onClick={openAddModal} colorScheme="blue" className="whitespace-nowrap shadow">
                    Add <i className="fa-solid fa-plus pl-2"></i>
                  </Button>
                </Tooltip>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-3">
              {isInitialLoading ? (
                <>
                  <Box className="border p-4 sm:p-5 bg-white shadow-sm rounded-lg">
                    <SkeletonCircle size="14" mb="4" />
                    <SkeletonText noOfLines={4} spacing="3" skeletonHeight="3" />
                  </Box>
                  <Box className="border p-4 sm:p-5 bg-white shadow-sm rounded-lg">
                    <SkeletonCircle size="14" mb="4" />
                    <SkeletonText noOfLines={4} spacing="3" skeletonHeight="3" />
                  </Box>
                </>
              ) : Array.isArray(projects) && projects.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6">
                  <EmptyState
                    title="Projects"
                    icon={FaCode}
                    emptyMessage="Start by adding your Projects"
                    addButtonText="Add Your Projects"
                    onAdd={openAddModal}
                  />
                </div>
              ) : (
                projects &&
                projects?.map((project) => (
                  <div
                    key={project.uuid}
                    className="group relative border rounded-xl shadow-sm hover:shadow-md transition-all duration-200 bg-white overflow-hidden"
                  >
                    {/* Accent bar */}
                    <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-blue-500 to-indigo-500" />
                    <div className="p-4 md:p-5">
                      <div className="flex justify-between items-start mb-2 gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="capitalize text-base sm:text-lg font-semibold text-gray-900 leading-tight break-words">
                              {project?.project_tag}
                            </div>
                            {project?.project_status && (
                              <Badge
                                colorScheme={
                                  project.project_status === "Completed"
                                    ? "green"
                                    : project.project_status === "Ongoing"
                                      ? "blue"
                                      : "gray"
                                }
                                variant="subtle"
                                className="rounded-full"
                              >
                                {project.project_status}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-3 flex-shrink-0">
                          <Tooltip label="Edit Project">
                            <Button colorScheme="blue" onClick={() => openUpdateModal(project)} className="shadow-sm">
                              <i className="fa-sharp fa-solid fa-pen"></i>
                            </Button>
                          </Tooltip>
                          <Tooltip label="Delete Project">
                            <Button onClick={() => openDeleteModal(project)} colorScheme="red" variant="outline">
                              <i className="cursor-pointer fa-solid fa-trash "></i>
                            </Button>
                          </Tooltip>
                        </div>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="leading-none flex gap-2 items-center flex-wrap">
                          <span className="text-gray-500 font-medium">Role:</span>
                          <span className="text-gray-800 font-medium">{project?.role}</span>
                        </div>
                        <div className="leading-none flex gap-2 items-center flex-wrap">
                          <span className="text-gray-500 font-medium">Skills Used:</span>
                          <span className="font-medium text-blue-700 break-words">{project?.skill_used}</span>
                        </div>
                        <div className="leading-snug">
                          <span className="text-gray-500 font-medium">Role Description:</span>
                          <p className="text-gray-800 mt-1 break-words">{project?.role_description}</p>
                        </div>
                        <div className="leading-snug">
                          <span className="text-gray-500 font-medium">Project Description:</span>
                          <p className="text-gray-800 mt-1 break-words">{project?.project_description}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Single Project Modal for Add/Update */}
      <Modal
        isOpen={isProjectModalOpen}
        onClose={handleCloseProjectModal}
        closeOnOverlayClick={!loading}
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxW="5xl">
          {/* Disable native browser tooltips */}
          <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
            <ModalHeader>{isUpdate ? "Edit Project" : "Add Project"}</ModalHeader>
            <ModalCloseButton isDisabled={loading} />
            <ModalBody pb={6}>
              <FormControl isRequired isInvalid={!!errors.project_tag}>
                <FormLabel>Project Title</FormLabel>
                <Input
                  placeholder="Enter Project title"
                  aria-invalid={!!errors.project_tag}
                  {...register("project_tag", {
                    required: "Project title is required",
                    minLength: { value: 3, message: "Must be at least 3 characters" },
                    maxLength: { value: 120, message: "Must be 120 characters or less" },
                    setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                  })}
                />
                <FormErrorMessage>{errors.project_tag && errors.project_tag.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.project_status}>
                <FormLabel>Project Status</FormLabel>
                <Select
                  placeholder="Select Project Status"
                  aria-invalid={!!errors.project_status}
                  {...register("project_status", {
                    required: "Project status is required",
                    validate: (v) =>
                      v === "Ongoing" || v === "Completed" || v === "On Hold" || v === "Cancelled" || "Please select Ongoing or Completed",
                  })}
                >
                  <option value={"Ongoing"}>Ongoing</option>
                  <option value={"Completed"}>Completed</option>
                  <option value={"On Hold"}>On Hold</option>
                  <option value={"Cancelled"}>Cancelled</option>
                </Select>
                <FormErrorMessage>{errors.project_status && errors.project_status.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.role}>
                <FormLabel>Your Role in the Project</FormLabel>
                <Input
                  placeholder="Enter Your Role"
                  aria-invalid={!!errors.role}
                  {...register("role", {
                    required: "Your Role in the Project is required",
                    minLength: { value: 2, message: "Must be at least 2 characters" },
                    maxLength: { value: 80, message: "Must be 80 characters or less" },
                    setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                  })}
                />
                <FormErrorMessage>{errors.role && errors.role.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.skill_used}>
                <FormLabel>Skills Used</FormLabel>
                <Input
                  placeholder="Enter Skills used"
                  aria-invalid={!!errors.skill_used}
                  {...register("skill_used", {
                    required: "Skills Used is required",
                    minLength: { value: 2, message: "Must be at least 2 characters" },
                    maxLength: { value: 200, message: "Must be 200 characters or less" },
                    setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                  })}
                />
                <FormErrorMessage>{errors.skill_used && errors.skill_used.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.role_description}>
                <FormLabel>Role Description</FormLabel>
                <Input
                  placeholder="Enter Role Description"
                  aria-invalid={!!errors.role_description}
                  {...register("role_description", {
                    required: "Role Description is required",
                    minLength: { value: 10, message: "Must be at least 10 characters" },
                    maxLength: { value: 500, message: "Must be 500 characters or less" },
                    setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                  })}
                />
                <FormErrorMessage>{errors.role_description && errors.role_description.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.project_description}>
                <FormLabel>Project Description</FormLabel>
                <Input
                  placeholder="Enter Project Description"
                  aria-invalid={!!errors.project_description}
                  {...register("project_description", {
                    required: "Project Description is required",
                    minLength: { value: 10, message: "Must be at least 10 characters" },
                    maxLength: { value: 1000, message: "Must be 1000 characters or less" },
                    setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                  })}
                />
                <FormErrorMessage>
                  {errors.project_description && errors.project_description.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.employment_type}>
                <FormLabel>Employee Type</FormLabel>
                <Select
                  placeholder="Select Employment Type"
                  aria-invalid={!!errors.employment_type}
                  {...register("employment_type", {
                    required: "Employment type is required",
                    validate: (v) => v === "Full Time" || v === "Part Time" || v === "Contract" || v === "Freelance" || v === "Internship" || "Please select a valid type",
                  })}
                >
                  <option value={"Full Time"}>Full Time</option>
                  <option value={"Part Time"}>Part Time</option>
                  <option value={"Contract"}>Contract</option>
                  <option value={"Freelance"}>Freelance</option>
                  <option value={"Internship"}>Internship</option>
                </Select>
                <FormErrorMessage>{errors.employment_type && errors.employment_type.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.team_size}>
                <FormLabel>Team Size</FormLabel>
                <Input
                  placeholder="Enter Team Size"
                  aria-invalid={!!errors.team_size}
                  {...register("team_size", {
                    required: "Team Size is required",
                    validate: validateTeamSize,
                    setValueAs: (v) => (v == null ? "" : String(v).trim()),
                  })}
                />
                <FormErrorMessage>{errors.team_size && errors.team_size.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.work_from_year}>
                <FormLabel>Starting Year</FormLabel>
                <Select
                  placeholder="Select Year"
                  aria-invalid={!!errors.work_from_year}
                  {...register("work_from_year", {
                    required: "Starting Year is required",
                    validate: validateYear,
                  })}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.work_from_year && errors.work_from_year.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.work_from_month}>
                <FormLabel>Starting Month</FormLabel>
                <Select
                  placeholder="Select Month"
                  aria-invalid={!!errors.work_from_month}
                  {...register("work_from_month", {
                    required: "Starting Month is required",
                    validate: validateMonth,
                  })}
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.work_from_month && errors.work_from_month.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.work_till_year}>
                <FormLabel>Ending Year</FormLabel>
                <Select
                  placeholder="Select Year"
                  aria-invalid={!!errors.work_from_year}
                  {...register("work_till_year", {
                    required: "Ending Year is required",
                    validate: validateYear,
                  })}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.work_till_year && errors.work_till_year.message}</FormErrorMessage>
              </FormControl>
              <FormControl mt={4} isRequired isInvalid={!!errors.work_till_month}>
                <FormLabel>Ending Month</FormLabel>
                <Select
                  placeholder="Select Month"
                  aria-invalid={!!errors.work_till_month}
                  {...register("work_till_month", {
                    required: "Starting Month is required",
                    validate: validateMonth,
                  })}
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.work_till_month && errors.work_till_month.message}</FormErrorMessage>
              </FormControl>
            </ModalBody>


            {loading && <SpinnerLoad />}

            <ModalFooter className="bg-logoblue">
              <Button mr={3} type="submit" isDisabled={loading}>
                {isUpdate ? "Update" : "Save"}
              </Button>
              <Button onClick={handleCloseProjectModal} isDisabled={loading}>
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Project Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose} >
        <ModalOverlay />
        <ModalContent maxW={"3xl"}>
          <div className="p-4">
            <Alert status="warning" borderRadius="md" variant="left-accent" mb={3}>
              <AlertIcon />
              This action cannot be undone.
            </Alert>
            <h4 className="py-2 text-sm sm:text-base text-gray-800">
              Are you sure you want to delete{" "}
              <span className="font-semibold">"{deletingProject?.project_tag}"</span>?
            </h4>
            {loading && <SpinnerLoad />}
            <div className="w-full flex justify-center gap-2 mt-4 relative">
              <Button
                colorScheme="red"
                className="w-48"
                onClick={() => deletingProject && handleDelete(deletingProject.uuid)}
                isDisabled={loading || !deletingProject}
              >
                Delete
              </Button>
              <Button
                colorScheme="red"
                textColor="red"
                variant="outline"
                className="w-48 bg-transparent"
                onClick={onDeleteModalClose}
                isDisabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </div>
  )
}